<script setup>
import { useCompanyInfoStore } from '@/store/company'
import { RouterLink } from 'vue-router'

const company = useCompanyInfoStore()
const fallbackAbout = 'Usenlease connects equipment owners with people who need them. Rent anything from power tools to party supplies — all in one place.'

const values = [
  { icon: 'pi-verified', title: 'Trust & Safety', desc: 'Verified users, secure payments, and full insurance coverage on every rental.' },
  { icon: 'pi-bolt', title: 'Fast & Easy', desc: 'Browse, book, and get your equipment delivered in just a few taps.' },
  { icon: 'pi-heart', title: 'Community First', desc: 'Built for people helping people — share what you own, rent what you need.' },
  { icon: 'pi-headset', title: '24/7 Support', desc: 'Our team is always available to help with booking, returns, or any questions.' },
]

const stats = [
  { value: '10K+', label: 'Active Users' },
  { value: '25K+', label: 'Items Listed' },
  { value: '50K+', label: 'Rentals Completed' },
  { value: '4.8', label: 'Avg Rating' },
]
</script>

<template>
  <div class="bg-theme-bg min-h-screen">
    <!-- Hero Section -->
    <div class="relative overflow-hidden">
      <div class="absolute inset-0 bg-gradient-to-br from-primary-600/10 via-transparent to-secondary-500/10"></div>
      <div class="max-w-5xl mx-auto px-4 pt-12 pb-16 sm:pt-16 sm:pb-20 relative z-10">
        <div class="text-center">
          <span class="inline-flex items-center gap-1.5 bg-primary-50 dark:bg-primary-600/10 text-primary-600 dark:text-primary-400 text-xs font-semibold px-3 py-1 rounded-full mb-4">
            <i class="pi pi-info-circle text-[10px]"></i> About Us
          </span>
          <h1 class="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-theme-text tracking-tight leading-tight mb-4">
            Rent Anything,<br class="hidden sm:block" />
            <span class="text-primary-600">From Anyone Nearby</span>
          </h1>
          <p class="text-base sm:text-lg text-theme-text2 max-w-2xl mx-auto leading-relaxed"
            v-html="company.companyInfo?.about || fallbackAbout">
          </p>
        </div>
      </div>
    </div>

    <div class="max-w-5xl mx-auto px-4 pb-16 space-y-12">
      <!-- Stats Row -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div v-for="stat in stats" :key="stat.label"
          class="bg-theme-card border border-theme-border rounded-2xl p-5 text-center">
          <p class="text-2xl sm:text-3xl font-extrabold text-primary-600">{{ stat.value }}</p>
          <p class="text-sm text-theme-muted mt-1">{{ stat.label }}</p>
        </div>
      </div>

      <!-- Values -->
      <div>
        <h2 class="text-xl sm:text-2xl font-bold text-theme-text text-center mb-8">What We Stand For</h2>
        <div class="grid sm:grid-cols-2 gap-4">
          <div v-for="v in values" :key="v.title"
            class="bg-theme-card border border-theme-border rounded-2xl p-5 flex gap-4 items-start hover:shadow-md transition-shadow">
            <div class="w-11 h-11 rounded-xl bg-primary-50 dark:bg-primary-600/10 flex items-center justify-center shrink-0">
              <i :class="['pi', v.icon, 'text-lg text-primary-600']"></i>
            </div>
            <div>
              <h3 class="text-base font-bold text-theme-text mb-1">{{ v.title }}</h3>
              <p class="text-sm text-theme-text2 leading-relaxed">{{ v.desc }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- How It Works -->
      <div>
        <h2 class="text-xl sm:text-2xl font-bold text-theme-text text-center mb-8">How It Works</h2>
        <div class="grid sm:grid-cols-3 gap-4">
          <div class="bg-theme-card border border-theme-border rounded-2xl p-6 text-center">
            <div class="w-12 h-12 rounded-full bg-primary-600 text-white flex items-center justify-center text-lg font-bold mx-auto mb-3">1</div>
            <h3 class="text-base font-bold text-theme-text mb-1">Browse & Book</h3>
            <p class="text-sm text-theme-text2">Find what you need, pick your dates, and add to cart.</p>
          </div>
          <div class="bg-theme-card border border-theme-border rounded-2xl p-6 text-center">
            <div class="w-12 h-12 rounded-full bg-primary-600 text-white flex items-center justify-center text-lg font-bold mx-auto mb-3">2</div>
            <h3 class="text-base font-bold text-theme-text mb-1">Pay Securely</h3>
            <p class="text-sm text-theme-text2">Checkout with confidence — payments are protected.</p>
          </div>
          <div class="bg-theme-card border border-theme-border rounded-2xl p-6 text-center">
            <div class="w-12 h-12 rounded-full bg-primary-600 text-white flex items-center justify-center text-lg font-bold mx-auto mb-3">3</div>
            <h3 class="text-base font-bold text-theme-text mb-1">Use & Return</h3>
            <p class="text-sm text-theme-text2">Pick up or get it delivered, then return when done.</p>
          </div>
        </div>
      </div>

      <!-- CTA -->
      <div class="bg-gradient-to-r from-primary-600 to-primary-500 rounded-2xl p-8 sm:p-10 text-center text-white">
        <h2 class="text-xl sm:text-2xl font-bold mb-2">Ready to get started?</h2>
        <p class="text-sm sm:text-base text-white/80 mb-6 max-w-lg mx-auto">Join thousands of users renting and listing equipment every day.</p>
        <div class="flex flex-col sm:flex-row gap-3 justify-center">
          <RouterLink to="/categories"
            class="px-6 py-3 bg-white text-primary-600 font-bold rounded-xl hover:bg-white/90 transition text-sm">
            Browse Items
          </RouterLink>
          <RouterLink to="/contact"
            class="px-6 py-3 bg-white/15 text-white font-bold rounded-xl hover:bg-white/25 transition text-sm border border-white/20">
            Contact Us
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>
